import { StrictMode, useState, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { AppProvider } from './context'
import './index.css'
import App from './App.jsx'
import LoginPage from './components/auth/LoginPage.jsx'
import EtlDesignerView from './components/etl/EtlDesignerView.jsx'

// Initialize API URL from environment (set by .env file)
if (import.meta.env.VITE_API_URL) {
  window.__MODERNIZEIT_API_URL__ = import.meta.env.VITE_API_URL
  console.log(' API URL configured from .env:', window.__MODERNIZEIT_API_URL__)
}

// Auth Wrapper - shows login or main app based on auth state
function AuthWrapper() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check if user is already logged in.
    // A real JWT is always > 100 chars. Reject short/garbage values ("null", "temp-token", etc.)
    // that were previously stored and caused a stuck-authenticated state with broken 401 loops.
    const raw   = localStorage.getItem('modernizeit_token')
    const token = raw && raw.length > 100 ? raw : null
    const user  = localStorage.getItem('modernizeit_user')
    if (token && user) {
      console.log(' [AuthWrapper] Token found in localStorage - setting authenticated')
      setIsAuthenticated(true)
    } else {
      if (raw && !token) {
        console.warn(' [AuthWrapper] Discarding invalid token from localStorage (length:', raw.length, ')')
        localStorage.removeItem('modernizeit_token')
      }
    }
    setIsLoading(false)
  }, [])

  // ✅ CRITICAL FIX: Listen for auth-logout event to handle token expiry
  useEffect(() => {
    const handleLogout = (event) => {
      const reason = event.detail?.reason || 'unknown'
      console.warn(` [AuthWrapper] Auth logout event received - reason: ${reason}`)
      console.log(' [AuthWrapper] Clearing authentication state and redirecting to login')
      setIsAuthenticated(false)
      // Token already cleared by the component that dispatched the event
    }

    window.addEventListener('auth-logout', handleLogout)
    return () => window.removeEventListener('auth-logout', handleLogout)
  }, [])

  const handleLoginSuccess = (userData) => {
    console.log(' [AuthWrapper] Login successful - setting authenticated')
    if (userData.token) localStorage.setItem('modernizeit_token', userData.token)
    localStorage.setItem('modernizeit_user', JSON.stringify(userData.user))
    setIsAuthenticated(true)
  }

  // Standalone ETL mode — bypass auth entirely
  const standaloneParam = new URLSearchParams(window.location.search).get('standalone')
  if (standaloneParam === 'etl') {
    return <EtlDesignerView onBack={() => { window.location.href = '/' }} />
  }

  if (isLoading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0a2540', color: '#fff' }}>Loading...</div>
  }

  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />
  }

  return (
    <AppProvider>
      <App />
    </AppProvider>
  )
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <AuthWrapper />
  </StrictMode>,
)
